import React from "react";

import DatePicker from "react-datepicker";
import moment from "moment";
import Modal from "react-responsive-modal";

import { DEFAULT_RELATIVE_DUE_DATE } from "../../../constants/EngagementTemplates";
import EngagementTaskModal from "../EngagementTemplates/eng_task_modal/EngagementTaskModal";
import UtilityService from "../../../services/UtilityService";

import SearchableDropdown from "../../common/searchable_dropdown/SearchableDropdown";
import UserService from "../../../services/UserService";
import EngagementService from "../../../services/EngagementService";
import ClientService from "../../../services/ClientService";
import WComponent from "../../common/WComponent";
import TextFieldInput from "../../common/text_field_input/TextFieldInput";
import UserAssigner from "../../common/user_assigner/UserAssigner";
import Tabs from "../../common/tabs/Tabs";
import Tab from "../../common/tabs/tab/Tab";
import RRuleEditor from "../../common/RRule/RRuleEditor";
import EngagementTaskCard from "../../common/eng_task_card/EngagementTaskCard";
import RelativeDate from "../../common/RelativeDate/RelativeDate";
import DateSelector from "../../common/date_selector/DateSelector";
import Button from "../../common/button/Button";
import Checkbox from "../../common/checkbox/Checkbox";
import AddItemButton from "../../common/add_item_button/AddItemButton";

import "./engagement-modal.css";
import RequiredLabel from "../../common/required_label";
import EngagementBudgetSettings from "../eng_budget/Eng_Budget";
import EngagementBudgetCard from "../../common/eng_budget_card/EngagementBudgetCard";

const TABS = {
	tasks: "tasks",
	users: "users",
	repeats: "repeats",
	budgets: "budgets"
};

const { alert } = UtilityService;

class EngagementModal extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			loading: false,
			show: false,

			teamId: null,
			flowId: null,
			flowStateId: null,
			clientId: 0,
			name: "",
			dateIn: moment(new Date()).format("YYYY-MM-DD hh:mm:ss a"),
			dueDate: moment(new Date()).format("YYYY-MM-DD hh:mm:ss a"),
			fiscalYearEnd: new Date(),
			notes: "",

			// Eng Task Data
			engagementTaskAction: "new",
			engTaskUpdateIndex: null,
			showEngagementTaskModal: false,

			flows: [],
			flowStates: [],

			clientName: "",
			selectedTeamName: "",
			selectedTab: "tasks",
			selectedFlowStateName: "",
			tasks: [],
			assignedUsers: [],
			roles: [],
			budgets: [],
			rrule: "",
			relativeDueDate: DEFAULT_RELATIVE_DUE_DATE,
			taxEstimate: false
		};

		this.clientSearchableDropdown = null;
	}

	componentDidMount() {
		this.resetComponent();
	}

	componentDidUpdate(prevProps) {
		let { show, flowId } = this.props;

		if (prevProps.show !== show || prevProps.flowId !== flowId) {
			this.resetComponent();
		}
	}

	resetComponent = async () => {
		let { show, clientId, flowId } = this.props;

		await this.update({ loading: true });

		await this.update({
			show,
			flowId,
			clientId: clientId || 0,
			clientName: ""
		});

		await this.setFlow();

		if (clientId) {
			let client = await ClientService.fetchClient({ clientId });

			await this.update({
				clientName: client.client.name
			});
		}

		await this.fetchAllRoles();
		await this.update({ loading: false });
	};

	fetchAllRoles = async () => {
		let roles = await UserService.fetchRoles({});

		await this.update({
			roles: roles
		});
	};

	onDateInSelect = date => {
		this.update({
			dateIn: date
		});
	};

	onDueDateSelect = date => {
		this.update({
			dueDate: date
		});
	};

	onFiscalYearEndSelect = async date => {
		await this.update({
			fiscalYearEnd: date
		});

		this.setName();
		this.setDueDate();
	};

	setFlow = async ({ flow } = {}) => {
		let { flowId } = this.state;

		if (!flow) {
			flow = await EngagementService.fetchFlow({ flowId });
		}

		await this.update({
			flowId: flow.id,
			flowCode: flow.code,
			flowName: flow.name,

			flowStateId: null,
			selectedFlowStateName: ""
		});
	};

	onFlowSelect = async flow => {
		let { flowId } = this.state;

		// User selected same flow, dipset
		if (flowId === flow.id) {
			return;
		}

		await this.setFlow({ flow });

		this.setName();
		this.setDueDate();
	};

	onFlowStateSelect = async flowState => {
		await this.update({
			flowStateId: flowState.id
		});
	};

	setDueDate = () => {
		let { flowCode, fiscalYearEnd } = this.state;
		let dueDate = EngagementService.deriveDueDateForEngagement({ flowCode, fiscalYearEnd });
		this.update({
			dueDate
		});
	};

	setName() {
		let { flowName, clientName, fiscalYearEnd } = this.state;

		let newName = `${flowName} - ${clientName} - ${moment(fiscalYearEnd).format("YYYY-MM-DD")}`;

		this.update({
			name: newName
		});
	}

	onTabSelect = async selectedTab => {
		await this.update({ selectedTab: selectedTab.id });
	};

	clearModal = async () => {
		await this.update({
			name: "",

			teamId: 0,
			clientId: 0,
			// flowId: null,
			flowStateId: null,

			fiscalYearEnd: new Date(),
			dateIn: new Date(),
			dueDate: new Date(),

			assignedUsers: [],

			selectedTeamName: "",
			selectedFlowStateName: "",
			tasks: [],
			budgets: [],
			taxEstimate: false
		});

		this.clientSearchableDropdown.clear();
	};

	createEngagement = async () => {
		let {
			name,
			teamId,
			clientId,
			flowId,
			flowStateId,
			dateIn,
			dueDate,
			fiscalYearEnd,
			assignedUsers,
			tasks,
			rrule,
			relativeDueDate,
			loading,
			taxEstimate,
			budgets
		} = this.state;

		let created = false;

		const missing = this.getMissingRequiredFields();
		if (missing.length) {
			alert({ type: "error", text: `Please fill: ${missing.join(", ")}` });
			return created;
		}

		if (loading) {
			alert({ type: "info", text: "Updating ..." });
			return;
		}

		await this.update({ loading: true });

		try {
			let { data } = await EngagementService.create({
				name,

				teamId,
				clientId,
				flowId,
				flowStateId,

				dueDate: moment(dueDate).format("YYYY-MM-DD"),
				dateIn: moment(dateIn).format("YYYY-MM-DD"),
				fiscalYearEnd: moment(fiscalYearEnd).format("YYYY-MM-DD"),

				assignedUsers,
				tasks,
				budgets: budgets,

				rrule,
				relativeDueDate,
				taxEstimate
			});

			this.clearModal();

			created = data.id;
		} catch (error) {
			console.log(error);

			created = null;
		}

		await this.update({ loading: false });

		return created;
	};

	onAddBudget = async () => {
		if (!this.budgetSettingsRef) return;
		const { resource = "", budget = "" } = this.budgetSettingsRef.state || {};
		const trimmedResource = (resource || "").trim();
		const trimmedBudget = (budget || "").trim();
		if (!trimmedResource || !trimmedBudget) {
			UtilityService.alert({ type: "error", text: "Please enter Resource and Budget time." });
			return;
		}
		const next = this.state.budgets.slice();
		next.push({ resource: trimmedResource, budget: trimmedBudget });
		await this.update({ budgets: next });
		this.budgetSettingsRef.update({ resource: "", budget: "" });
	};

	onRemoveBudget = (index) => {
		const next = this.state.budgets.slice();
		next.splice(index, 1);
		this.update({ budgets: next });
	};


	onCreate = async () => {
		let created = await this.createEngagement();

		if (created && this.props.onCreate) {
			this.update({
				show: false
			});

			this.props.onCreate(created);
		}
	};

	onClose = async () => {
		if (this.props.onClose) {
			this.props.onClose();
		}

		this.clearModal();
	};

	onClientSelect = async item => {
		await this.update({
			clientId: item.id,
			clientName: item.name
		});

		this.setName();
	};

	isValid = () => this.getMissingRequiredFields().length === 0;

	onChange = e => {
		let { name, value, type, checked } = e.target;

		if (type === "checkbox") {
			value = checked;
		}

		this.update({
			[name]: value
		});
	};

	onAssignedUsersChanged = async ({ selectedUsers }) => {
		let newAssignedUsers = selectedUsers.slice();

		await this.update({
			assignedUsers: newAssignedUsers
		});
	};

	onSelectTeam = async team => {
		let { onFetchUsersByTeam } = this;
		// update the team
		await this.update({
			teamId: team.id,
			selectedTeamName: team.name
		});

		// fetch users associated with the team
		await onFetchUsersByTeam({ teamId: team.id });
	};

	onFetchUsersByTeam = async ({ teamId }) => {
		let team = await UserService.fetchTeam({ teamId });

		// update the assigend users of the selected new team
		await this.update({
			assignedUsers: team.users
		});
	};

	getFlowStatusName = async ({ flowId, flowStateId }) => {
		let flowStatusName = "";
		let flowStates = await EngagementService.fetchFlowStates({ flowId });

		// loop through the flow state and get the flow state name that matches the flow state id
		for (let i = 0; i < flowStates.length; i++) {
			if (flowStates[i].id === flowStateId) {
				flowStatusName = flowStates[i].name;
			}
		}

		return flowStatusName;
	};

	findRole = idToQuery => {
		let { roles } = this.state;

		const result = roles.find(item => item.id === idToQuery);
		let name = "";

		if (result) {
			name = result.name;
		} else {
			console.log("Object not found with ID:", idToQuery);
		}

		return name;
	};

	onSelectTemplate = async template => {
		let team = await UserService.fetchTeam({ teamId: template.team_id });

		await this.update({
			flowId: template.flow_id
		});

		await this.setFlow();

		await this.update({
			teamId: template.team_id,
			selectedTeamName: team.name || "",

			flowStateId: template.flow_state_id,
			selectedFlowStateName: await this.getFlowStatusName({ flowId: template.flow_id, flowStateId: template.flow_state_id }),

			assignedUsers: team.users,
			tasks: JSON.parse(template.tasks)
		});

		await this.setName();
		await this.setDueDate();
	};

	onRRuleChange = async ({ rrule }) => {
		await this.update({ rrule });
	};

	onNewEngTask = async () => {
		await this.update({
			engagementTaskAction: "new",
			engTaskUpdateIndex: null,
			showEngagementTaskModal: true
		});
	};

	onUpdateEngTask = async index => {
		await this.update({
			engagementTaskAction: "update",
			engTaskUpdateIndex: index,
			showEngagementTaskModal: true
		});
	};

	onRemoveTask = index => {
		let { tasks } = this.state;
		let prevTasks = tasks;
		prevTasks.splice(index, 1);

		this.update({
			tasks: prevTasks
		});
	};

	onRelativeDueDateChange = async relativeDueDate => {
		await this.update({ relativeDueDate });
	};

	getMissingRequiredFields = () => {
		const { name, clientId, flowId, flowStateId, dateIn, dueDate, fiscalYearEnd } = this.state;

		const missing = [];

		if (!name?.trim()) missing.push("Name");
		if (!clientId) missing.push("Client");
		if (!flowId) missing.push("Workflow Type");	
		if (!flowStateId) missing.push("Status");
		if (!fiscalYearEnd) missing.push("Fiscal Year End");
		if (!dateIn) missing.push("Date In");
		if (!dueDate) missing.push("Due Date");

		return missing;
	};

	render() {
		let {
			// View Toggles
			show,

			// Engagement Data
			name,
			fiscalYearEnd,
			dateIn,
			dueDate,
			taxEstimate,
			tasks,
			assignedUsers,

			rrule,
			relativeDueDate,

			// Eng Task Data
			showEngagementTaskModal,
			engagementTaskAction,
			engTaskUpdateIndex,

			// Display Items & Dirty Bits
			clientName,
			flowName,
			flowCode,
			selectedTeamName,
			selectedFlowStateName,
			selectedTemplate,
			selectedTab
		} = this.state;

		return (
			<Modal open={show} onClose={this.onClose} center>
				<EngagementTaskModal
					show={showEngagementTaskModal}
					action={engagementTaskAction}
					onClose={() => {
						this.update({
							engagementTaskAction: "new",
							engTaskUpdateIndex: null,
							showEngagementTaskModal: false
						});
					}}
					engTasks={tasks}
					taskToUpdateIndex={engTaskUpdateIndex}
				/>

				<div className="eng-modal">
					<div className="modal-title">New Engagement</div>
					<div className="eng-modal__body">
						<div className="eng-modal__body__fields">
							<div className="eng-modal__body__fields__container">
								<SearchableDropdown
									title="Template"
									onSelect={this.onSelectTemplate}
									type={"engTemplate"}
									selectedName={selectedTemplate}
									showAll
									className={"ft-eng-mod-template"}
								/>

								<TextFieldInput title={<RequiredLabel text="Name (Will Auto Populate)" />} name="name" onChange={this.onChange} autoComplete="off" value={name} />

								<SearchableDropdown
									title={<RequiredLabel text="Client" />}
									ref={ref => (this.clientSearchableDropdown = ref)}
									selectedName={clientName}
									onSelect={this.onClientSelect}
									type={"clients"}
									className={"ft-eng-mod-client"}
								/>
								<SearchableDropdown
									title="Team"
									onSelect={this.onSelectTeam}
									type={"teams"}
									selectedName={selectedTeamName}
									showAll
									className={"ft-eng-mod-team"}
								/>

								<Checkbox
									title="Tax Estimate"
									name="taxEstimate"
									checked={taxEstimate}
									onChange={this.onChange}
									className={"ft-eng-mod-taxest"}
								/>
							</div>

							<div className="eng-modal__body__fields__container">
								<SearchableDropdown
									showAll
									title={<RequiredLabel text="Workflow Type" />}
									onSelect={this.onFlowSelect}
									selectedName={flowName}
									type={"flows"}
									className={"ft-eng-mod-flow"}
								/>

								<SearchableDropdown
									showAll
									title={<RequiredLabel text="Status" />}									
									type="flowStates"
									flowCode={flowCode}
									selectedName={selectedFlowStateName}
									onSelect={this.onFlowStateSelect}
									className={"ft-eng-mod-flowstate"}
								/>

								<DateSelector
									// TODO: this classname/component will be refactored in the final theme fixes pr
									containerClass="eng-modal__date-selector"
									title={<RequiredLabel text="Fiscal Year End" />}
									selectedDate={new Date(fiscalYearEnd)}
									onDateSelect={this.onFiscalYearEndSelect}
									className={"ft-eng-mod-fiscyearend"}
								/>

								<DateSelector
									containerClass="eng-modal__date-selector"
									title={<RequiredLabel text="Date In" />}									
									selectedDate={dateIn !== "" ? new Date(dateIn) : new Date()}
									onDateSelect={this.onDateInSelect}
									className={"ft-eng-mod-datein"}
								/>

								<DateSelector
									title={<RequiredLabel text="Due Date" />}
									selectedDate={dueDate !== "" ? new Date(dueDate) : new Date()}
									onDateSelect={this.onDueDateSelect}
									className={"ft-eng-mod-duedate"}
								/>
							</div>
						</div>

						<div className="eng-modal__body__settings">
							<Tabs className="eng-modal__body__settings__tabs" onSelect={this.onTabSelect} selected={selectedTab}>
								<Tab id={TABS.tasks} text="Tasks" />
								<Tab id={TABS.users} text="Users" />
								<Tab id={TABS.repeats} text="Repeat Settings" />
								<Tab id={TABS.budgets} text="Budgets" />
							</Tabs>

							{selectedTab === TABS.tasks && (
								<div className="eng-modal__body__settings__tasks">
									<div className="eng-modal__body__settings__tasks__list">
										{tasks.map((task, index) => (
											<EngagementTaskCard
												key={index}
												task={task}
												index={index}
												onRemoveTask={this.onRemoveTask}
												onUpdateEngTask={this.onUpdateEngTask}
											/>
										))}
									</div>

									<AddItemButton itemName="Task" onClick={this.onNewEngTask} />
								</div>
							)}

							{selectedTab === TABS.users && (
								<UserAssigner containerClass="eng-modal__body__user-assigner" onChange={this.onAssignedUsersChanged} users={assignedUsers} />
							)}
							{selectedTab === TABS.repeats && (
								<>
									<RRuleEditor onChange={this.onRRuleChange} rrule={rrule} />
									{rrule.length > 0 && (
										<>
											<div className="eng-modal__body__settings__divider" />
											<div className="eng-modal__body__settings__repeats__section">
												<RelativeDate title="Recurring Due Date" onChange={this.onRelativeDueDateChange} data={relativeDueDate} />
											</div>
										</>
									)}
								</>
							)}
							{selectedTab === TABS.budgets && (
								<div className="eng-modal__body__settings__budgets">
									<EngagementBudgetSettings ref={(ref) => (this.budgetSettingsRef = ref)} />

									<div className="eng-modal__body__settings__divider" />

									<div className="eng-modal__body__settings__budgets__list">
										{this.state.budgets.map((b, idx) => (
											<EngagementBudgetCard key={idx} budget={b} index={idx} onRemoveBudget={this.onRemoveBudget} />
										))}
									</div>

									<AddItemButton itemName="Budget" onClick={this.onAddBudget} />
								</div>
							)}
						</div>
					</div>
				</div>
				<div className="modal-footer">
					<Button view="secondary" text="Cancel" onClick={this.onClose} />
					<Button text="Create" onClick={this.onCreate} disabled={this.state.loading} />
				</div>
			</Modal>
		);
	}
}

export default EngagementModal;
